import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const GARNET = '#5b1e22';
const COAL = '#181617';
const ASHEN = '#7A766F';
const OYSTER = '#F7F7F7';

const STATUS_STYLES = {
  pending:  { background: '#fff8e1', color: '#8a6400', border: '1px solid #ffe082' },
  approved: { background: '#e8f5e9', color: '#1a7a4c', border: '1px solid #a5d6a7' },
  rejected: { background: '#fce4ec', color: '#b32d2d', border: '1px solid #ef9a9a' },
};

function BriefCard({ brief, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState(brief.suggested_rules || []);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setRules(brief.suggested_rules || []);
  }, [brief]);

  const handleApprove = async () => {
    setSubmitting(true);
    await onApprove(brief.id, rules);
    setSubmitting(false);
  };

  const handleRuleChange = (i, value) => {
    setRules(prev => prev.map((r, idx) => idx === i ? { ...r, rule: value } : r));
  };

  const isPending = brief.status === 'pending';

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
      {/* Header */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ padding: '1rem 1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '0.2rem 0.6rem', borderRadius: 20, ...STATUS_STYLES[brief.status] }}>
            {brief.status.toUpperCase()}
          </span>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: COAL }}>
              Week of {brief.week_start} — {brief.week_end}
            </div>
            <div style={{ fontSize: '0.75rem', color: ASHEN }}>
              {brief.rejection_count} rejections · {(brief.suggested_rules || []).length} suggestions
              {brief.reviewed_by && ` · Reviewed by ${brief.reviewed_by}`}
            </div>
          </div>
        </div>
        <span style={{ color: ASHEN, fontSize: '1rem', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </div>

      {/* Expanded content */}
      {open && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '1.2rem' }}>

          {/* Summary */}
          <div style={{ marginBottom: '1.2rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Reflector's Brief
            </div>
            <div style={{ background: OYSTER, borderRadius: 8, padding: '0.9rem 1rem', fontSize: '0.85rem', color: COAL, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {brief.summary}
            </div>
          </div>

          {/* Suggested rules */}
          {rules.length > 0 && (
            <div style={{ marginBottom: '1.2rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Suggested Rules {isPending && <span style={{ fontWeight: 400, textTransform: 'none', color: ASHEN }}>(edit before approving)</span>}
              </div>
              {rules.map((r, i) => (
                <div key={i} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: 8 }}>
                  <div style={{ fontSize: '0.7rem', color: ASHEN, marginBottom: 6 }}>
                    <strong>Why:</strong> {r.reason}
                  </div>
                  {isPending ? (
                    <textarea
                      value={r.rule}
                      onChange={e => handleRuleChange(i, e.target.value)}
                      rows={3}
                      style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.6rem', borderRadius: 6, border: '1px solid #d1d5db', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', color: COAL }}
                    />
                  ) : (
                    <div style={{ fontSize: '0.82rem', color: COAL, lineHeight: 1.5 }}>{r.rule}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          {isPending && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleApprove}
                disabled={submitting}
                style={{ padding: '0.5rem 1.1rem', background: GARNET, color: '#fff', border: 'none', borderRadius: 7, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
              >
                {submitting ? 'Applying…' : `Approve & Apply ${rules.length} Rule${rules.length !== 1 ? 's' : ''}`}
              </button>
              <button
                onClick={() => onReject(brief.id)}
                disabled={submitting}
                style={{ padding: '0.5rem 1rem', background: '#f3f4f6', color: COAL, border: 'none', borderRadius: 7, fontSize: '0.82rem', cursor: 'pointer' }}
              >
                Reject
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function rateColor(rate) {
  if (rate === null) return ASHEN;
  if (rate >= 90) return '#1a7a4c';
  if (rate >= 75) return '#8a6400';
  return '#b32d2d';
}

function rateBg(rate) {
  if (rate === null) return '#f3f4f6';
  if (rate >= 90) return '#e8f5e9';
  if (rate >= 75) return '#fff8e1';
  return '#fce4ec';
}

function AcceptanceStats() {
  const [weeks, setWeeks] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    api.get('/messages/ai-stats')
      .then(res => setWeeks(res.data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []);

  if (statsLoading) return null;
  if (!weeks.length) return null;

  // Most recent week with actioned messages (skip current week if all still pending)
  const latestActioned = weeks.find(w => w.total_actioned > 0);
  const currentRate = latestActioned?.acceptance_rate ?? null;

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.1rem 1.2rem', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', color: COAL }}>Acceptance Rate</div>
          <div style={{ fontSize: '0.75rem', color: ASHEN }}>How often staff approve AI drafts without skipping — target is 90% for autonomous mode</div>
        </div>
        {currentRate !== null && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: '1.8rem', fontWeight: 700, color: rateColor(currentRate), lineHeight: 1 }}>
              {currentRate}%
            </div>
            <div style={{ fontSize: '0.7rem', color: ASHEN, marginTop: 2 }}>most recent week</div>
            {currentRate >= 90 && (
              <div style={{ fontSize: '0.7rem', color: '#1a7a4c', fontWeight: 600, marginTop: 3 }}>✓ Ready to review for autonomous</div>
            )}
          </div>
        )}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: ASHEN, fontWeight: 600 }}>Week</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: ASHEN, fontWeight: 600 }}>Accepted</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: ASHEN, fontWeight: 600 }}>Skipped</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: ASHEN, fontWeight: 600 }}>Pending</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: ASHEN, fontWeight: 600 }}>Rate</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map(w => {
            const d = new Date(w.week_start);
            const label = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
            return (
              <tr key={w.week_start} style={{ borderBottom: '1px solid #f9fafb' }}>
                <td style={{ padding: '0.45rem 0.5rem', color: COAL }}>{label}</td>
                <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', color: COAL }}>{w.accepted}</td>
                <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', color: w.skipped > 0 ? '#b32d2d' : ASHEN }}>{w.skipped}</td>
                <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', color: ASHEN }}>{w.pending || '—'}</td>
                <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>
                  {w.acceptance_rate !== null ? (
                    <span style={{ fontWeight: 600, color: rateColor(w.acceptance_rate), background: rateBg(w.acceptance_rate), padding: '0.15rem 0.5rem', borderRadius: 6 }}>
                      {w.acceptance_rate}%
                    </span>
                  ) : (
                    <span style={{ color: ASHEN }}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const REJECT_CATEGORIES = [
  ['wrong_info', 'Wrong info'], ['tone', 'Tone / voice'], ['too_long', 'Too long'],
  ['no_reply_needed', 'No reply needed'], ['stale', 'Customer sent more'],
  ['booking_mistake', 'Booking mistake'], ['missed_question', 'Missed the question'], ['other', 'Other'],
];

const REPLAY_MODELS = [
  ['', 'Current (as configured)'],
  ['claude-sonnet-4-6', 'Sonnet 4.6 (today)'],
  ['claude-sonnet-5', 'Sonnet 5'],
  ['claude-opus-5', 'Opus 5'],
];

function ReplayPanel() {
  const [model, setModel] = useState('');
  const [thinking, setThinking] = useState('');
  const [n, setN] = useState(6);
  const [category, setCategory] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const run = async () => {
    setRunning(true); setErr(''); setResult(null);
    try {
      const res = await api.post('/reflector/replay', { n, model: model || null, thinking: thinking || null, category: category || null });
      setResult(res.data);
    } catch (e) { setErr(e.response?.data?.error || 'Replay failed'); }
    finally { setRunning(false); }
  };

  const sel = { padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.82rem', color: COAL };
  const cell = { fontSize: '0.78rem', color: COAL, whiteSpace: 'pre-wrap', lineHeight: 1.45 };
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.1rem 1.2rem', marginBottom: '1.5rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: COAL }}>Replay skipped drafts under a candidate model</div>
      <div style={{ fontSize: '0.75rem', color: ASHEN, marginBottom: '0.8rem' }}>
        Re-runs Lily on recent skipped drafts exactly as the conversation stood then (nothing is saved or sent). Compare her old draft, what staff sent instead, and the new draft.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={model} onChange={e => setModel(e.target.value)} style={sel}>
          {REPLAY_MODELS.map(([v, l]) => <option key={v || 'cur'} value={v}>{l}</option>)}
        </select>
        <select value={thinking} onChange={e => setThinking(e.target.value)} style={sel}>
          <option value="">Thinking: as configured</option>
          <option value="off">Thinking: off</option>
          <option value="adaptive">Thinking: adaptive</option>
        </select>
        <select value={category} onChange={e => setCategory(e.target.value)} style={sel}>
          <option value="">Any skip reason</option>
          {REJECT_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={n} onChange={e => setN(Number(e.target.value))} style={sel}>
          {[3, 6, 8].map(v => <option key={v} value={v}>{v} cases</option>)}
        </select>
        <button onClick={run} disabled={running} style={{ padding: '0.5rem 1.1rem', background: GARNET, color: '#fff', border: 'none', borderRadius: 7, fontSize: '0.82rem', fontWeight: 600, cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.7 : 1 }}>
          {running ? 'Replaying… (~30s)' : 'Replay'}
        </button>
      </div>
      {err && <div style={{ marginTop: 10, fontSize: '0.8rem', color: '#b32d2d' }}>{err}</div>}
      {result && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ fontSize: '0.75rem', color: ASHEN, marginBottom: 8 }}>
            New drafts by <strong>{result.model}</strong> (thinking: {result.thinking}) · baseline is {result.baseline_model}
          </div>
          {result.results.map(r => (
            <div key={r.message_id} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10, marginTop: 10 }}>
              <div style={{ fontSize: '0.7rem', color: ASHEN, marginBottom: 6 }}>
                {new Date(r.when).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · {r.customer} · {r.country}
                {r.rejection_category && <span style={{ marginLeft: 8, background: '#fee2e2', color: '#b32d2d', borderRadius: 999, padding: '0.1rem 0.5rem', fontWeight: 600 }}>{r.rejection_category}</span>}
              </div>
              <div style={{ ...cell, marginBottom: 6 }}><span style={{ color: ASHEN }}>Customer: </span>{r.customer_message}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                <div style={{ background: '#fff5f5', borderRadius: 6, padding: '0.5rem 0.6rem' }}><div style={{ fontSize: '0.68rem', color: '#b32d2d', fontWeight: 700, marginBottom: 3 }}>OLD DRAFT (skipped)</div><div style={cell}>{r.old_draft}</div></div>
                <div style={{ background: '#f0fdf4', borderRadius: 6, padding: '0.5rem 0.6rem' }}><div style={{ fontSize: '0.68rem', color: '#1a7a4c', fontWeight: 700, marginBottom: 3 }}>STAFF SENT</div><div style={cell}>{r.sent_instead || r.rejection_reason || '—'}</div></div>
                <div style={{ background: '#f5f3ff', borderRadius: 6, padding: '0.5rem 0.6rem' }}><div style={{ fontSize: '0.68rem', color: '#5b1e22', fontWeight: 700, marginBottom: 3 }}>NEW DRAFT</div><div style={cell}>{r.new_draft}</div></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RejectionBreakdown({ refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/messages/rejections/summary').then(r => setData(r.data)).catch(() => {});
  }, [refreshKey]);
  if (!data || !data.all_time?.length) return null;
  const total = data.all_time.reduce((s, r) => s + r.n, 0);
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.1rem 1.2rem', marginBottom: '1.5rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: COAL }}>Why drafts get skipped</div>
      <div style={{ fontSize: '0.75rem', color: ASHEN, marginBottom: '0.8rem' }}>All-time, by category — the fix for each is different, so this is what to work on</div>
      {data.all_time.map(r => (
        <div key={r.category} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.25rem 0', fontSize: '0.8rem' }}>
          <div style={{ width: 150, color: r.category === 'uncategorised' ? ASHEN : COAL, fontStyle: r.category === 'uncategorised' ? 'italic' : 'normal' }}>{r.label}</div>
          <div style={{ flex: 1, height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${(r.n / total) * 100}%`, height: '100%', background: r.category === 'uncategorised' ? '#d1d5db' : GARNET }} />
          </div>
          <div style={{ width: 70, textAlign: 'right', color: ASHEN }}>{r.n} · {Math.round((r.n / total) * 100)}%</div>
        </div>
      ))}
    </div>
  );
}

function RejectionBacklog({ onChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(null);
  const load = () => api.get('/messages/rejections/uncategorised?limit=30')
    .then(r => { setItems(r.data.items); setTotal(r.data.total_uncategorised); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const categorise = async (id, category) => {
    setBusy(id);
    try {
      await api.post(`/messages/${id}/categorise`, { category });
      setItems(prev => prev.filter(i => i.id !== id));
      setTotal(t => Math.max(0, t - 1));
      onChange && onChange();
    } catch (e) { /* leave it in the list */ }
    finally { setBusy(null); }
  };

  if (!total) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.1rem 1.2rem', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', color: COAL }}>Skipped drafts needing a reason</div>
          <div style={{ fontSize: '0.75rem', color: ASHEN }}>One tap each. Read the customer's message, Lily's draft, and what was sent instead — then say why it was skipped.</div>
        </div>
        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#8a6400', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 7, padding: '0.25rem 0.7rem' }}>{total} left</div>
      </div>
      {items.map(it => (
        <div key={it.id} style={{ borderTop: '1px solid #f0f0f0', marginTop: '0.8rem', paddingTop: '0.8rem', opacity: busy === it.id ? 0.5 : 1 }}>
          <div style={{ fontSize: '0.7rem', color: ASHEN, marginBottom: 4 }}>
            {new Date(it.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · {it.customer_name} · {it.country}
          </div>
          {it.customer_message && <div style={{ fontSize: '0.78rem', color: COAL, marginBottom: 4 }}><span style={{ color: ASHEN }}>Customer: </span>{it.customer_message}</div>}
          <div style={{ fontSize: '0.78rem', color: COAL, marginBottom: 4, background: '#faf7f5', borderRadius: 6, padding: '0.4rem 0.6rem' }}><span style={{ color: ASHEN }}>Lily: </span>{it.draft}</div>
          {it.rejection_reason && <div style={{ fontSize: '0.78rem', color: '#1a7a4c', marginBottom: 6 }}><span style={{ color: ASHEN }}>Sent instead / note: </span>{it.rejection_reason}</div>}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {REJECT_CATEGORIES.map(([key, label]) => (
              <button key={key} disabled={busy === it.id} onClick={() => categorise(it.id, key)} style={{
                padding: '0.25rem 0.55rem', borderRadius: 999, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer',
                border: '1px solid #e5e7eb', background: '#fff', color: COAL
              }}>{label}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CSAgentTraining() {
  const [breakdownKey, setBreakdownKey] = useState(0);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [daysBack, setDaysBack] = useState(7);

  const loadBriefs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/reflector/briefs');
      setBriefs(res.data);
    } catch (e) {
      setError('Could not load briefs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBriefs(); }, [loadBriefs]);

  useEffect(() => {
    if (!error && !success) return;
    const t = setTimeout(() => { setError(''); setSuccess(''); }, 4000);
    return () => clearTimeout(t);
  }, [error, success]);

  const handleRun = async () => {
    setRunning(true);
    setError('');
    try {
      await api.post('/reflector/run', { days_back: daysBack });
      setSuccess('Analysis complete — new brief added.');
      loadBriefs();
    } catch (e) {
      setError(e.response?.data?.error || 'Analysis failed.');
    } finally {
      setRunning(false);
    }
  };

  const handleApprove = async (briefId, rules) => {
    try {
      await api.post(`/reflector/briefs/${briefId}/approve`, { suggested_rules: rules });
      setSuccess('Rules approved and applied to Shari.');
      loadBriefs();
    } catch (e) {
      setError(e.response?.data?.error || 'Approval failed.');
    }
  };

  const handleReject = async (briefId) => {
    try {
      await api.post(`/reflector/briefs/${briefId}/reject`);
      setSuccess('Brief rejected.');
      loadBriefs();
    } catch (e) {
      setError('Rejection failed.');
    }
  };

  const pendingCount = briefs.filter(b => b.status === 'pending').length;

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: '1.5rem 1rem' }}>

      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: COAL, margin: 0 }}>CS Agent Training</h1>
        <p style={{ fontSize: '0.82rem', color: ASHEN, margin: '0.3rem 0 0' }}>
          The Reflector analyses rejected AI drafts and suggests improvements to Shari's rules.
          Review each brief, edit suggestions if needed, then approve to apply them.
        </p>
      </div>

      {/* Toast */}
      {(error || success) && (
        <div style={{ padding: '0.7rem 1rem', borderRadius: 8, marginBottom: 16, fontSize: '0.82rem', background: error ? '#fee2e2' : '#e8f5e9', color: error ? '#b32d2d' : '#1a7a4c' }}>
          {error || success}
        </div>
      )}

      {/* Acceptance Rate Stats */}
      <AcceptanceStats />

      {/* Why drafts get skipped + the backlog of uncategorised skips */}
      <RejectionBreakdown refreshKey={breakdownKey} />
      <RejectionBacklog onChange={() => setBreakdownKey(k => k + 1)} />

      {/* Run Analysis panel */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.1rem 1.2rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', color: COAL }}>Run Analysis</div>
          <div style={{ fontSize: '0.75rem', color: ASHEN }}>Analyses all rejected AI drafts in the selected window and produces a new brief.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select
            value={daysBack}
            onChange={e => setDaysBack(Number(e.target.value))}
            style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.82rem', color: COAL }}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button
            onClick={handleRun}
            disabled={running}
            style={{ padding: '0.5rem 1.1rem', background: GARNET, color: '#fff', border: 'none', borderRadius: 7, fontSize: '0.82rem', fontWeight: 600, cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.7 : 1 }}
          >
            {running ? 'Analysing…' : 'Run Now'}
          </button>
        </div>
      </div>

      {/* Replay: judge a candidate model/prompt on evidence before it goes live */}
      <ReplayPanel />

      {/* Briefs list */}
      {loading ? (
        <div style={{ textAlign: 'center', color: ASHEN, padding: '2rem', fontSize: '0.85rem' }}>Loading…</div>
      ) : briefs.length === 0 ? (
        <div style={{ textAlign: 'center', color: ASHEN, padding: '3rem 1rem' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: COAL, marginBottom: 4 }}>No briefs yet</div>
          <div style={{ fontSize: '0.8rem' }}>Skip some AI drafts with reasons, then run the first analysis.</div>
        </div>
      ) : (
        <>
          {pendingCount > 0 && (
            <div style={{ fontSize: '0.78rem', color: '#8a6400', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 7, padding: '0.5rem 0.9rem', marginBottom: 12 }}>
              {pendingCount} brief{pendingCount > 1 ? 's' : ''} awaiting your review
            </div>
          )}
          {briefs.map(b => (
            <BriefCard
              key={b.id}
              brief={b}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </>
      )}
    </div>
  );
}
